import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { z } from 'zod';
import { keyValueStorage, useKeyValueStorageItem } from '@orionprotocol/common';

import { LanguageList } from '../../../shared/language';
import { LanguageContext } from '../../../shared/context';

const defaultLanguage = LanguageList.EN;

const getLocale = (language: LanguageList): string => language.toLowerCase();

export function LanguageProvider({
  children,
}: PropsWithChildren<unknown>): JSX.Element {
  const languageFromLS = useKeyValueStorageItem('language', defaultLanguage);

  const parsedLanguage = useMemo(() => {
    const result = z.nativeEnum(LanguageList).safeParse(languageFromLS);
    if (result.success) {
      return result.data;
    }
    return defaultLanguage;
  }, [languageFromLS]);

  const [language, setLanguage] = useState<LanguageList>(parsedLanguage);

  const changeLanguage = useCallback(async (nextLanguage: LanguageList) => {
    try {
      const languageStr = getLocale(nextLanguage);

       
      const { messages } = await import(
        `../../../shared/language/locales/${languageStr}/messages.po`
      );

       
      i18n.load(languageStr, messages);
      i18n.activate(languageStr);

      if (typeof nextLanguage === 'string') {
        keyValueStorage.setItem('language', nextLanguage);
      }

      setLanguage(nextLanguage);
    } catch (err) {
      console.log('translation loading error', err);
    }
  }, []);

  useEffect(() => {
    if (language !== undefined) {
      void changeLanguage(language);
    }
  }, [changeLanguage, language]);

  const contextProviderValue = useMemo(
    () => ({ language, changeLanguage, getLocale }),
    [changeLanguage, language],
  );

  return (
    <LanguageContext.Provider value={contextProviderValue}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LanguageContext.Provider>
  );
}
